import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getPayout,
  holdPayout,
  mockMarkPaid,
  payNow,
  schedulePayout,
  unholdPayout,
} from '../../api/adminPayoutsApi';
import { useToast } from '../../components/ToastContext';
import { useCurrency } from '../../components/CurrencyContext';

const badgeStyles = {
  PENDING: 'bg-amber-50 text-amber-700 ring-amber-100',
  ON_HOLD: 'bg-rose-50 text-rose-700 ring-rose-100',
  SCHEDULED: 'bg-sky-50 text-sky-700 ring-sky-100',
  PROCESSING: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
  PAID: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-100',
  CANCELLED: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const statusLabel = (value) => String(value || 'unknown').replace(/_/g, ' ').toLowerCase();

const PayoutBadge = ({ status }) => {
  const key = String(status || '').toUpperCase();
  const cls = badgeStyles[key] || 'bg-slate-100 text-slate-700 ring-slate-200';
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${cls}`}>{statusLabel(key)}</span>;
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const toDateTimeInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`;
};

const AdminPayoutDetail = ({ token }) => {
  const { formatCurrency } = useCurrency();
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scheduleValue, setScheduleValue] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const formatMoney = (currency, amount) => {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) return 'N/A';
    const value = numeric / 100;
    return formatCurrency(value, currency || 'INR') || `${currency || 'INR'} ${value}`;
  };
  const resolveAmount = (row) => (row?.amountPaise ?? row?.amount);
  const [holdReason, setHoldReason] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getPayout(token, id);
      setData(res);
      setScheduleValue(toDateTimeInput(res?.payout?.scheduledFor || res?.payout?.scheduled_for));
      setHoldReason(res?.payout?.holdReason || res?.payout?.hold_reason || '');
    } catch (err) {
      setError(err.message || 'Failed to load payout');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const payout = data?.payout || null;
  const account = data?.account || null;
  const host = payout?.host || {};
  const payment = payout?.payment || null;
  const hostName = host?.name || host?.full_name || payout?.host_name || 'Host';
  const hostEmail = host?.email || host?.phone || payout?.host_email || 'n/a';
  const payoutRef = payout?.payoutRef || payout?.reference || `#${payout?.id || ''}`;
  const payoutCreatedAt = payout?.createdAt || payout?.created_at;
  const payoutScheduledFor = payout?.scheduledFor || payout?.scheduled_for;
  const payoutPaidAt = payout?.paidAt || payout?.paid_at;
  const payoutBookingId = payout?.bookingId || payout?.booking_id;
  const payoutPaymentId = payout?.paymentId || payout?.payment_id;
  const payoutFailureReason = payout?.failureReason || payout?.failure_reason;
  const payoutHoldReason = payout?.holdReason || payout?.hold_reason;
  const payoutGatewayTransferId = payout?.gatewayTransferId || payout?.gateway_transfer_id;

  const payoutStatus = String(payout?.status || '').toUpperCase();
  const canHold = payout && !['PAID', 'FAILED', 'CANCELLED', 'ON_HOLD'].includes(payoutStatus);
  const canUnhold = payoutStatus === 'ON_HOLD';
  const canSchedule = payout && !['PAID', 'FAILED', 'CANCELLED'].includes(payoutStatus);
  const canPayNow = payout && !['PAID', 'FAILED', 'CANCELLED', 'ON_HOLD'].includes(payoutStatus);
  const canMockPaid = payout && !['PAID', 'FAILED', 'CANCELLED'].includes(payoutStatus);

  const runAction = async (action, handler) => {
    setActionLoading(action);
    try {
      await handler();
      await load();
      toast.success('Payout updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update payout');
    } finally {
      setActionLoading('');
    }
  };

  if (error) {
    return (
      <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-rose-200">
        <p className="text-rose-700">{error}</p>
        <button
          type="button"
          onClick={() => navigate('/admin/payouts')}
          className="mt-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Back to payouts
        </button>
      </div>
    );
  }

  if (loading || !data) {
    return <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">Loading...</div>;
  }

  return (
    <div className="admin-card space-y-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-start justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Payout</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">{payoutRef}</h1>
          <p className="text-sm text-slate-600">
            Host: {hostName} ({hostEmail})
          </p>
        </div>
        <PayoutBadge status={payout?.status} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-lg border border-slate-200 p-3">
          <p className="text-sm font-semibold text-slate-900">Amount</p>
          <p className="text-lg font-bold text-slate-900">{formatMoney(payout?.currency, resolveAmount(payout))}</p>
        </div>
        <div className="rounded-lg border border-slate-200 p-3">
          <p className="text-sm font-semibold text-slate-900">Created</p>
          <p className="text-sm text-slate-700">{formatDateTime(payoutCreatedAt)}</p>
        </div>
        <div className="rounded-lg border border-slate-200 p-3">
          <p className="text-sm font-semibold text-slate-900">Scheduled</p>
          <p className="text-sm text-slate-700">{formatDateTime(payoutScheduledFor)}</p>
        </div>
        <div className="rounded-lg border border-slate-200 p-3">
          <p className="text-sm font-semibold text-slate-900">Paid</p>
          <p className="text-sm text-slate-700">{formatDateTime(payoutPaidAt)}</p>
        </div>
        <div className="rounded-lg border border-slate-200 p-3">
          <p className="text-sm font-semibold text-slate-900">Payment</p>
          <p className="text-sm text-slate-700">{payment?.paymentRef || payoutPaymentId || 'N/A'}</p>
        </div>
        <div className="rounded-lg border border-slate-200 p-3">
          <p className="text-sm font-semibold text-slate-900">Gateway transfer</p>
          <p className="text-sm text-slate-700">{payoutGatewayTransferId || 'N/A'}</p>
        </div>
        <div className="rounded-lg border border-slate-200 p-3">
          <p className="text-sm font-semibold text-slate-900">Booking</p>
          <p className="text-sm text-slate-700">{payoutBookingId || 'N/A'}</p>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 p-3">
        <p className="text-sm font-semibold text-slate-900">Bank account</p>
        {account ? (
          <div className="mt-2 text-sm text-slate-700">
            <p>Status: {account.status}</p>
            <p>
              {account.accountHolder} - {account.bankName}
            </p>
            <p className="text-xs text-slate-500">Account: {account.accountNumber}</p>
          </div>
        ) : (
          <p className="mt-2 text-sm text-slate-500">No bank account on file.</p>
        )}
      </div>

      {payoutHoldReason && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
          Hold reason: {payoutHoldReason}
        </div>
      )}
      {payoutFailureReason && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          Failure reason: {payoutFailureReason}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 p-3">
        <p className="text-sm font-semibold text-slate-900">Actions</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <input
              type="text"
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              placeholder="Hold reason (required to hold)"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              if (!holdReason.trim()) {
                toast.error('Provide a hold reason');
                return;
              }
              runAction('hold', () => holdPayout(token, id, { holdReason: holdReason.trim() }));
            }}
            disabled={!canHold || actionLoading}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {actionLoading === 'hold' ? 'Holding...' : 'Hold'}
          </button>
          <button
            type="button"
            onClick={() => runAction('unhold', () => unholdPayout(token, id))}
            disabled={!canUnhold || actionLoading}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {actionLoading === 'unhold' ? 'Unholding...' : 'Unhold'}
          </button>
          <div className="sm:col-span-2 flex flex-col gap-2 sm:flex-row">
            <input
              type="datetime-local"
              value={scheduleValue}
              onChange={(e) => setScheduleValue(e.target.value)}
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => {
                if (!scheduleValue) {
                  toast.error('Select a schedule date and time');
                  return;
                }
                runAction('schedule', () => schedulePayout(token, id, { scheduledFor: scheduleValue }));
              }}
              disabled={!canSchedule || !scheduleValue || actionLoading}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
            >
              {actionLoading === 'schedule' ? 'Scheduling...' : 'Schedule'}
            </button>
          </div>
          <button
            type="button"
            onClick={() => runAction('pay', () => payNow(token, id))}
            disabled={!canPayNow || actionLoading}
            className="sm:col-span-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {actionLoading === 'pay' ? 'Paying...' : 'Pay now (mock gateway)'}
          </button>
          <button
            type="button"
            onClick={() => runAction('paid', () => mockMarkPaid(token, id))}
            disabled={!canMockPaid || actionLoading}
            className="sm:col-span-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {actionLoading === 'paid' ? 'Marking paid...' : 'Mark paid (testing)'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPayoutDetail;
